import clsx from "clsx";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { RadioButtonOption } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { ChangeTwoFactorAuthenticationMethod } from "@/services/model/changeTwoFactorAuthenticationMethod";

export type AuthenticatorSetupMethodsForm = {
  type: keyof typeof ChangeTwoFactorAuthenticationMethod | null;
};

/**
 * Small security-level chip. The pill sits on the neutral `surface-quaternary`
 * background; only the label colour changes: `info` for the secure options,
 * `warning` for the not-recommended one.
 */
const SecurityChip = ({
  text,
  variant,
}: {
  text: string;
  variant: "info" | "warning";
}) => (
  <span
    className={clsx(
      "inline-flex items-center rounded-full bg-surface-quaternary px-2 py-1 text-xs font-semibold leading-none tracking-[0.24px]",
      variant === "info" ? "text-foreground-info" : "text-foreground-warning",
    )}
  >
    {text}
  </span>
);

const OptionLabel = ({ title, chip }: { title: string; chip: ReactNode }) => (
  <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
    <span>{title}</span>
    {chip}
  </span>
);

export const AuthenticatorSetupFormElements = () => (
  <>
    <TwoFactorAppOption />
    <TwoFactorSmsOption />
    <TwoFactorStandardOption />
  </>
);

/** Every option below must render inside an ancestor `RadioGroup` (see the form components that own `useForm`). */
export const TwoFactorAppOption = () => {
  const { t } = useTranslation("translation");

  return (
    <RadioButtonOption<AuthenticatorSetupMethodsForm>
      name="type"
      value={ChangeTwoFactorAuthenticationMethod.APP}
      label={
        <OptionLabel
          title={t("2fa.setup.options.authenticatorApp.title")}
          chip={
            <SecurityChip
              text={t("2fa.setup.options.very-secure")}
              variant="info"
            />
          }
        />
      }
    />
  );
};

export const TwoFactorSmsOption = () => {
  const { t } = useTranslation("translation");

  return (
    <RadioButtonOption<AuthenticatorSetupMethodsForm>
      name="type"
      value={ChangeTwoFactorAuthenticationMethod.SMS}
      label={
        <OptionLabel
          title={t("2fa.setup.options.sms.title")}
          chip={
            <SecurityChip text={t("2fa.setup.options.secure")} variant="info" />
          }
        />
      }
    />
  );
};

export const TwoFactorStandardOption = () => {
  const { t } = useTranslation("translation");

  return (
    <RadioButtonOption<AuthenticatorSetupMethodsForm>
      name="type"
      value={ChangeTwoFactorAuthenticationMethod.STANDARD}
      label={
        <OptionLabel
          title={t("2fa.setup.options.login.title")}
          chip={
            <SecurityChip
              text={t("2fa.setup.options.not-recommended")}
              variant="warning"
            />
          }
        />
      }
    />
  );
};
